import { parsePhoneNumberFromString } from "libphonenumber-js";

export const formatGhanaPhoneNumber = (phone) => {
  if (!phone) return "";
  
  const cleaned = phone.replace(/\D/g, "");
  
  if (cleaned.startsWith("233")) {
    return cleaned.slice(0, 12);
  }
  
  if (cleaned.startsWith("0")) {
    return "233" + cleaned.slice(1, 10);
  }
  
  if (cleaned.length === 9) {
    return "233" + cleaned;
  }
  
  return cleaned.startsWith("233") ? cleaned : "233" + cleaned;
};

/** Conference/contact (global): strip spaces; digits only, 10–15 length; no leading +. */
export const normalizeContactNumber = (value) => String(value || "").replace(/\s/g, "");

export const validateContactNumber = (value) => {
  const normalized = normalizeContactNumber(value);
  if (!normalized) {
    return { isValid: false, message: "Contact number is required" };
  }
  if (normalized.startsWith("+")) {
    return { isValid: false, message: "Do not include + in the contact number" };
  }
  if (!/^\d+$/.test(normalized)) {
    return { isValid: false, message: "Contact number must contain digits only" };
  }
  if (normalized.length < 10 || normalized.length > 15) {
    return { isValid: false, message: "Contact number must be between 10 and 15 digits" };
  }
  return { isValid: true, message: "" };
};

/** Accepts any international number: optional +, digits, spaces; at least 8 digits. */
export const validateInternationalPhone = (phone) => {
  if (!phone || !String(phone).trim()) return { isValid: false, message: "Contact number is required" };
  const digitsOnly = String(phone).replace(/\D/g, "");
  if (digitsOnly.length < 8) return { isValid: false, message: "Enter a valid phone number with at least 8 digits (include country code for international)" };
  if (digitsOnly.length > 18) return { isValid: false, message: "Phone number is too long" };

  const parsed = parsePhoneNumberFromString(`+${digitsOnly}`);
  if (!parsed) {
    return { isValid: false, message: "Enter a valid international phone number" };
  }

  const cc = parsed.countryCallingCode;
  const national = parsed.nationalNumber;
  // After choosing a country in the phone input, the calling code is already applied.
  // Reject when the typed national part again starts with those digits (e.g. 233233…, 9393…).
  if (cc && national && national.startsWith(cc)) {
    return {
      isValid: false,
      message:
        "Do not type the country code again. Pick the country and enter only the local number.",
    };
  }

  if (!parsed.isValid()) {
    return { isValid: false, message: "Enter a valid phone number for the selected country" };
  }

  return { isValid: true, message: "" };
};

export const validateGhanaPhoneNumber = (phone) => {
  if (!phone) return { isValid: false, message: "Phone number is required" };
  
  const formatted = formatGhanaPhoneNumber(phone);
  
  if (formatted.length !== 12) {
    return { 
      isValid: false, 
      message: "Phone number must be 12 digits (233XXXXXXXXX)" 
    };
  }
  
  if (!formatted.startsWith("233")) {
    return { 
      isValid: false, 
      message: "Phone number must start with 233" 
    };
  }
  
  const validPrefixes = ["20", "23", "24", "25", "26", "27", "28", "29", "50", "54", "55", "56", "57", "59"];
  const networkPrefix = formatted.substring(3, 5);
  
  if (!validPrefixes.includes(networkPrefix)) {
    return { 
      isValid: false, 
      message: "Invalid Ghana network prefix" 
    };
  }
  
  return { isValid: true, message: "", formatted };
};

export const formatPhoneOnInput = (value) => {
  const cleaned = value.replace(/\D/g, "");
  
  if (cleaned.startsWith("233")) {
    if (cleaned.length <= 3) return "233";
    if (cleaned.length <= 5) return `233 ${cleaned.slice(3)}`;
    if (cleaned.length <= 8) return `233 ${cleaned.slice(3, 5)} ${cleaned.slice(5)}`;
    return `233 ${cleaned.slice(3, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8, 12)}`;
  }
  
  if (cleaned.startsWith("0")) {
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
  }
  
  return cleaned;
};

export const extractInitials = (fullName) => {
  if (!fullName) return "";
  
  const names = fullName
    .trim()
    .split(/\s+/)
    .filter(name => name.length > 0);
  
  return names
    .map(name => name.charAt(0).toUpperCase())
    .join(".");
};

export const generateSignatureFormat = (fullName) => {
  const initials = extractInitials(fullName);
  return initials ? `${initials}.` : "";
};

export const validateSignature = (signature, applicantName) => {
  if (!signature) {
    return { 
      isValid: false, 
      message: "Signature is required" 
    };
  }
  
  if (!applicantName) {
    return { 
      isValid: false, 
      message: "Please enter applicant name first" 
    };
  }
  
  const expectedFormat = generateSignatureFormat(applicantName);
  const normalizedSignature = signature
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/\.+$/, ".");
  
  const normalizedExpected = expectedFormat
    .toUpperCase()
    .replace(/\s+/g, "");
  
  if (normalizedSignature !== normalizedExpected) {
    return { 
      isValid: false, 
      message: `Signature must be in format: ${expectedFormat}`,
      expectedFormat 
    };
  }
  
  return { isValid: true, message: "", expectedFormat };
};

